import { Head, Link, router, useForm } from '@inertiajs/react';
import AppLayout from '@/Layouts/AppLayout';
import Card, { CardBody } from '@/Components/Card';
import Button from '@/Components/Button';
import Badge from '@/Components/Badge';
import Table, { Th, Td, Tr } from '@/Components/Table';
import PageHeader from '@/Components/PageHeader';
import EmptyState from '@/Components/EmptyState';
import Modal, { ModalBody, ModalFooter } from '@/Components/Modal';
import FormInput from '@/Components/FormInput';
import Alert from '@/Components/Alert';
import { useState } from 'react';
import { PlusIcon, UserPlusIcon, PencilSquareIcon, TrashIcon, UsersIcon, EnvelopeIcon, MagnifyingGlassIcon, ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline';

export default function Index({ users, roles, invitations, filters, flash }) {
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const inviteForm = useForm({ email: '', role_id: '' });
    const [search, setSearch] = useState(filters?.search || '');

    const handleSearch = (e) => {
        e.preventDefault();
        router.get('/users', { search }, { preserveState: true });
    };

    const submitInvite = (e) => {
        e.preventDefault();
        inviteForm.post('/users/invite', { onSuccess: () => { setShowInviteModal(false); inviteForm.reset(); } });
    };

    const deleteUser = (id) => {
        router.delete(`/users/${id}`, { onSuccess: () => setDeleteConfirm(null) });
    };

    return (
        <AppLayout>
            <Head title="Users" />
            <PageHeader title="Users" subtitle="Manage system users and access" actions={
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setShowInviteModal(true)}><EnvelopeIcon className="w-4 h-4" /> Invite</Button>
                    <Button href="/users/create" size="sm"><PlusIcon className="w-4 h-4" /> Add User</Button>
                </div>
            } />

            {flash?.success && <Alert type="success" className="mb-4">{flash.success}</Alert>}

            {/* Search */}
            <Card className="mb-6">
                <CardBody>
                    <form onSubmit={handleSearch} className="flex gap-3">
                        <div className="relative flex-1">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                            <input type="text" placeholder="Search users..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:border-emerald-700 focus:ring-2 focus:ring-emerald-700/20" />
                        </div>
                        <Button type="submit" variant="primary" size="md">Search</Button>
                    </form>
                </CardBody>
            </Card>

            {/* Users Table */}
            <Card>
                <Table minWidth="700px">
                    <thead>
                        <tr><Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>Status</Th><Th>Created</Th><Th>Actions</Th></tr>
                    </thead>
                    <tbody>
                        {(Array.isArray(users) ? users : []).map(user => (
                            <Tr key={user.id}>
                                <Td><div className="flex items-center gap-3"><div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-900 font-bold text-sm">{user.name?.charAt(0).toUpperCase()}</div><span className="font-medium text-slate-800">{user.name}</span></div></Td>
                                <Td className="text-slate-500">{user.email}</Td>
                                <Td><Badge variant="info">{user.role_model?.name || 'N/A'}</Badge></Td>
                                <Td><Badge variant={user.is_active !== false ? 'success' : 'neutral'}>{user.is_active !== false ? 'Active' : 'Inactive'}</Badge></Td>
                                <Td className="text-slate-500 text-xs">{new Date(user.created_at).toLocaleDateString()}</Td>
                                <Td>
                                    <div className="flex items-center gap-1">
                                        <Link href={`/users/${user.id}/edit`} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"><PencilSquareIcon className="w-4 h-4 text-slate-500" /></Link>
                                        <button onClick={() => setDeleteConfirm(user.id)} className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"><TrashIcon className="w-4 h-4 text-red-400" /></button>
                                    </div>
                                </Td>
                            </Tr>
                        ))}
                    </tbody>
                </Table>
                {(!users || users.length === 0) && <EmptyState icon={UsersIcon} title="No users found" description="Create your first system user." action={<Button href="/users/create" size="sm"><PlusIcon className="w-4 h-4" /> Add User</Button>} />}
            </Card>

            {/* Invitations Section */}
            {invitations && invitations.length > 0 && (
                <Card className="mt-6">
                    <div className="px-6 py-4 border-b border-slate-100">
                        <h3 className="font-semibold text-slate-800">Pending Invitations</h3>
                    </div>
                    <Table minWidth="500px">
                        <thead><tr><Th>Email</Th><Th>Role</Th><Th>Sent</Th><Th>Actions</Th></tr></thead>
                        <tbody>
                            {invitations.map(inv => (
                                <Tr key={inv.id}>
                                    <Td className="font-medium">{inv.email}</Td>
                                    <Td><Badge variant="info">{inv.role?.name}</Badge></Td>
                                    <Td className="text-xs text-slate-500">{new Date(inv.created_at).toLocaleDateString()}</Td>
                                    <Td>
                                        <div className="flex gap-1">
                                            <button onClick={() => router.post(`/users/invitations/${inv.id}/resend`)} className="p-1.5 hover:bg-emerald-50 rounded-lg"><ArrowPathIcon className="w-4 h-4 text-emerald-800" /></button>
                                            <button onClick={() => router.delete(`/users/invitations/${inv.id}`)} className="p-1.5 hover:bg-red-50 rounded-lg"><XMarkIcon className="w-4 h-4 text-red-400" /></button>
                                        </div>
                                    </Td>
                                </Tr>
                            ))}
                        </tbody>
                    </Table>
                </Card>
            )}

            {/* Invite Modal */}
            <Modal show={showInviteModal} onClose={() => setShowInviteModal(false)} title="Invite User" icon={<EnvelopeIcon className="w-5 h-5" />}>
                <form onSubmit={submitInvite}>
                    <ModalBody className="space-y-4">
                        <FormInput label="Email" type="email" value={inviteForm.data.email} onChange={e => inviteForm.setData('email', e.target.value)} error={inviteForm.errors.email} required />
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                            <select value={inviteForm.data.role_id} onChange={e => inviteForm.setData('role_id', e.target.value)} className="w-full rounded-xl border border-slate-200 text-sm py-2.5 px-4 focus:border-emerald-700 focus:ring-2 focus:ring-emerald-700/20">
                                <option value="">Select role...</option>
                                {roles?.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                            </select>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="outline" onClick={() => setShowInviteModal(false)}>Cancel</Button>
                        <Button type="submit" disabled={inviteForm.processing}>Send Invitation</Button>
                    </ModalFooter>
                </form>
            </Modal>

            {/* Delete Confirm */}
            <Modal show={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Delete User" iconBg="bg-red-100" iconColor="text-red-600" icon={<TrashIcon className="w-5 h-5" />}>
                <ModalBody><p className="text-sm text-slate-600">Are you sure you want to delete this user? This action cannot be undone.</p></ModalBody>
                <ModalFooter>
                    <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
                    <Button variant="danger" onClick={() => deleteUser(deleteConfirm)}>Delete</Button>
                </ModalFooter>
            </Modal>
        </AppLayout>
    );
}
